import { z } from "zod";
import { startRunSchema } from "./runs";

/** Omitted limits are resolved from trusted project defaults when a rule fires. */
export const automatedRunSettingsSchema = startRunSchema
  .pick({ intent: true, branch: true })
  .extend({
    modelTier: startRunSchema.shape.modelTier.optional(),
    effort: startRunSchema.shape.effort.optional(),
    creditCap: startRunSchema.shape.creditCap.optional(),
  });
export type AutomatedRunSettings = z.infer<typeof automatedRunSettingsSchema>;

/** Schedule states, as the schedules table badges them. A paused schedule shows a caption. */
export const scheduleStates = ["active", "paused"] as const;
export const scheduleStateSchema = z.enum(scheduleStates);
export type ScheduleState = z.infer<typeof scheduleStateSchema>;

/**
 * One schedule, as `GET /api/v1/projects/:id/schedules` returns it. The pinned flow version is
 * the version runs start from; changing the schedule does not repin a run already in flight.
 * `nextRunAt` is UTC; the page renders it in the project time zone with UTC beneath.
 */
export const scheduleSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1),
  cron: z.string().min(1),
  flowVersion: z.number().int().min(1),
  flowId: z.uuid(),
  flowVersionId: z.uuid(),
  run: automatedRunSettingsSchema,
  state: scheduleStateSchema,
  nextRunAt: z.string().datetime().nullable(),
  consecutiveFailures: z.number().int().min(0),
  createdAt: z.string().datetime(),
});
export type Schedule = z.infer<typeof scheduleSchema>;

export const scheduleListSchema = z.array(scheduleSchema);
export type ScheduleList = z.infer<typeof scheduleListSchema>;

/** `POST /api/v1/projects/:id/schedules`. Creates one schedule. */
export const createScheduleSchema = z.object({
  name: z.string().trim().min(1).max(200),
  cron: z.string().trim().min(1).max(120),
  flowVersion: z.number().int().min(1),
  flowId: z.uuid(),
  flowVersionId: z.uuid(),
  run: automatedRunSettingsSchema,
});
export type CreateSchedule = z.infer<typeof createScheduleSchema>;

/**
 * A schedule pauses itself after 3 consecutive failures. The pause and resume endpoints take no
 * body; a request that carries one is ignored rather than trusted.
 */
export const scheduleLimitations = {
  consecutiveFailuresBeforePause: 3,
} as const;

/** Trigger sources, as the triggers table badges them. */
export const triggerSources = ["repository-event", "webhook"] as const;
export const triggerSourceSchema = z.enum(triggerSources);
export type TriggerSource = z.infer<typeof triggerSourceSchema>;

export const triggerStates = ["active", "paused", "failing"] as const;
export const triggerStateSchema = z.enum(triggerStates);
export type TriggerState = z.infer<typeof triggerStateSchema>;

/**
 * One trigger, as `GET /api/v1/projects/:id/triggers` returns it. A webhook carries a signing
 * secret: it is stored and never returned, so the response carries only whether one is set.
 */
export const triggerSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  source: triggerSourceSchema,
  event: z.string().min(1),
  flowVersion: z.number().int().min(1),
  flowId: z.uuid(),
  flowVersionId: z.uuid(),
  run: automatedRunSettingsSchema,
  state: triggerStateSchema,
  hasSigningSecret: z.boolean(),
  repositoryRef: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type Trigger = z.infer<typeof triggerSchema>;

export const triggerListSchema = z.array(triggerSchema);
export type TriggerList = z.infer<typeof triggerListSchema>;

/**
 * `POST /api/v1/projects/:id/triggers`. A webhook arrives with its signing secret in the body
 * exactly once, at creation. It is never echoed back by any endpoint.
 */
export const createTriggerSchema = z
  .object({
    source: triggerSourceSchema,
    event: z.string().trim().min(1).max(120),
    flowVersion: z.number().int().min(1),
    flowId: z.uuid(),
    flowVersionId: z.uuid(),
    run: automatedRunSettingsSchema,
    repositoryRef: z.string().trim().min(1).max(200).nullable().optional(),
    signingSecret: z.string().trim().min(16).max(400).optional(),
  })
  .refine((value) => value.source !== "webhook" || Boolean(value.signingSecret), {
    message: "A webhook requires a signing secret.",
    path: ["signingSecret"],
  });
export type CreateTrigger = z.infer<typeof createTriggerSchema>;

/** Delivery outcomes for the recent-deliveries list, bad by signature included. */
export const deliveryStates = [
  "delivered",
  "queued",
  "rejected-bad-signature",
  "failed",
  "started-run",
  "skipped-conditions",
  "skipped-budget",
  "skipped-duplicate",
  "skipped-quiet-hours",
  "skipped-concurrency",
  "skipped-loop-guard",
  "skipped-approval",
  "skipped-policy",
] as const;
export const deliveryStateSchema = z.enum(deliveryStates);
export type DeliveryState = z.infer<typeof deliveryStateSchema>;

/**
 * One recent delivery, so an owner can see that a bad signature started no run. A rejected
 * delivery records no run id, which is the evidence that nothing started.
 */
export const deliverySchema = z.object({
  id: z.uuid(),
  triggerId: z.uuid().nullable(),
  ruleId: z.uuid().nullable().optional(),
  state: deliveryStateSchema,
  runId: z.uuid().nullable(),
  receivedAt: z.string().datetime(),
  reason: z.string().nullable(),
});
export type Delivery = z.infer<typeof deliverySchema>;

export const deliveryListSchema = z.array(deliverySchema);
export type DeliveryList = z.infer<typeof deliveryListSchema>;

/** Headers are x-console-delivery-id, x-console-timestamp, and x-console-signature.
 * Sign the exact UTF-8 bytes of timestamp + "." + deliveryId + "." + raw request body with HMAC-SHA256.
 * Reject timestamps older than five minutes or more than one minute ahead; persist delivery ids
 * uniquely per trigger so a retry cannot reserve credits or start a second run.
 */
export const webhookDeliveryHeadersSchema = z.object({
  deliveryId: z.string().trim().min(1).max(120),
  timestamp: z.string().regex(/^\d{10}$/),
  signature: z.string().regex(/^sha256=[a-f0-9]{64}$/),
});
export type WebhookDeliveryHeaders = z.infer<typeof webhookDeliveryHeadersSchema>;

/** The signed JSON body identifies the event and, for repository rules, its bound repository.
 * Extra provider fields are preserved for condition evaluation; callers must verify event and
 * repository against the rule. Verification and the replay digest always use the exact
 * original request bytes. Optional lineage (ruleId, runId) is never matched, only used by
 * the loop guard so a rule never starts from its own event.
 */
export const automationWebhookPayloadSchema = z
  .object({
    event: z.string().trim().min(1).max(120),
    repositoryRef: z.string().trim().min(1).max(200).nullable().optional(),
    ruleId: z.string().trim().min(1).max(120).nullable().optional(),
    runId: z.string().trim().min(1).max(120).nullable().optional(),
  })
  .catchall(z.unknown());
export type AutomationWebhookPayload = z.infer<typeof automationWebhookPayloadSchema>;
